import { Edit } from "@mui/icons-material";
import { Button, Toolbar } from "@mui/material";
import React, { useState } from "react";
import { DataMapPatchMutation } from "../../../../common/hooks/api/useDataMap";
import { useDisclosureDetails } from "../../../../common/hooks/dataMapHooks";
import { CollectionGroupDetailsDto } from "../../../../common/service/server/dto/CollectionGroupDto";
import {
  PICGroupWithCategories,
  DataMapDto,
} from "../../../../common/service/server/dto/datamap/DataMapDto";
import { OrganizationDataRecipientDto } from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { CalloutVariant } from "../../../components/Callout";
import { DataRecipientCollapseCard } from "../../../components/DataRecipientCollapseCard";
import { Para } from "../../../components/typography/Para";
import { Paras } from "../../../components/typography/Paras";
import { DataRecipientCollectionCheckboxes } from "../../../pages/get-compliant/data-recipients/DataRecipientCollectionCheckboxes";
import { ConfirmDialog } from "../../dialog";
import { EmptyInfo } from "../../Empty";
import { PicGroupList } from "../collection-groups/PicGroupList";
import { Organization } from "../../../../common/models";

const CollectionContent: React.FC<{
  collectedPicGroups: PICGroupWithCategories[];
  dataRecipient: OrganizationDataRecipientDto;
}> = ({ dataRecipient, collectedPicGroups }) => {
  let disclosureType = dataRecipient.ccpaIsSelling ? "sold to" : "shared with";
  if (dataRecipient.dataRecipientType == "storage_location") {
    disclosureType = "stored in";
  }
  return collectedPicGroups.length > 0 ? (
    <Paras>
      <Para className="color-neutral-600">
        The following information is {disclosureType} <strong>{dataRecipient.name}</strong>
      </Para>

      <PicGroupList picGroups={collectedPicGroups} />
    </Paras>
  ) : (
    <EmptyInfo>
      No information is {disclosureType} <strong>{dataRecipient.name}</strong>
    </EmptyInfo>
  );
};

type DisclosureCardProps = {
  className?: string;
  dataMap: DataMapDto;
  patchDataMap: DataMapPatchMutation;
  collectionGroup: CollectionGroupDetailsDto;
  dataRecipient: OrganizationDataRecipientDto;
  afterSave: () => Promise<void>;
  forceEdit: boolean;
  org: Organization;
};

export const EditableDisclosureCard: React.FC<DisclosureCardProps> = ({
  className,
  dataMap,
  patchDataMap,
  collectionGroup,
  dataRecipient,
  afterSave,
  forceEdit = false,
  org,
}) => {
  const [editing, setEditing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isEditing = editing || forceEdit;

  const { disclosedPicGroups } = useDisclosureDetails(dataMap, collectionGroup, dataRecipient);

  const handleSave = async () => {
    if (disclosedPicGroups.length == 0) {
      setConfirmOpen(true);
    } else {
      await handleDone();
    }
  };

  const handleDone = async () => {
    await afterSave();
    setEditing(false);
  };

  return (
    <>
      <DataRecipientCollapseCard
        className={className}
        collapsible={false}
        recipient={dataRecipient}
        variant={isEditing ? CalloutVariant.Clear : CalloutVariant.Gray}
        headerContent={
          isEditing ? null : (
            <Button
              onClick={() => setEditing(true)}
              size="small"
              color="secondary"
              startIcon={<Edit />}
            >
              Edit
            </Button>
          )
        }
      >
        {isEditing ? (
          <>
            <DataRecipientCollectionCheckboxes
              dataMap={dataMap}
              patchDataMap={patchDataMap}
              group={collectionGroup}
              recipient={dataRecipient}
              org={org}
            />

            <Toolbar disableGutters>
              <Button className="ml-auto" variant="contained" color="primary" onClick={handleSave}>
                Save
              </Button>
            </Toolbar>
          </>
        ) : (
          <CollectionContent
            dataRecipient={dataRecipient}
            collectedPicGroups={disclosedPicGroups}
          />
        )}
      </DataRecipientCollapseCard>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        confirmText="Ok"
        confirmTitle="Are you sure?"
        contentText={`Removing all personal information categories for this data source will mark it as not associated for ${collectionGroup.name}`}
        onConfirm={handleDone}
      />
    </>
  );
};
